import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Users, ArrowLeft, Loader2, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Veuillez saisir votre adresse email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Email envoyé !');
    } catch (error) {
      console.error('Forgot password error:', error);
      // We show success anyway to avoid user enumeration
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container" data-testid="forgot-password-page">
      {/* Form section */}
      <div className="auth-form-section bg-slate-50">
        <div className="w-full max-w-md mx-auto">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Espace Employés</h1>
              <p className="text-sm text-slate-500">Portail de gestion</p>
            </div>
          </div>

          <Card className="border-0 shadow-lg">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl">Mot de passe oublié</CardTitle>
              <CardDescription>
                {sent 
                  ? "Vérifiez votre boîte de réception"
                  : "Entrez votre email pour réinitialiser votre mot de passe"
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sent ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Email envoyé !
                  </h3>
                  <p className="text-slate-500 mb-6">
                    Si cette adresse email est associée à un compte, vous recevrez un lien de réinitialisation.
                  </p>
                  <div className="space-y-3">
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => {
                        setSent(false);
                        setEmail('');
                      }}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Envoyer à une autre adresse
                    </Button>
                    <Link to="/login">
                      <Button className="w-full bg-slate-900 hover:bg-slate-800">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Retour à la connexion
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Adresse email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="nom@entreprise.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      data-testid="forgot-email-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-slate-900 hover:bg-slate-800"
                    disabled={loading}
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Envoyer le lien
                      </>
                    )}
                  </Button>

                  <div className="text-center">
                    <Link 
                      to="/login" 
                      className="text-sm text-slate-500 hover:text-slate-700 hover:underline inline-flex items-center gap-1"
                      data-testid="back-to-login"
                    >
                      <ArrowLeft className="w-3 h-3" />
                      Retour à la connexion
                    </Link>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Image section */}
      <div className="auth-image-section">
        <img
          src="https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMG1lZXRpbmclMjBkaXZlcnNlfGVufDB8fHx8MTc3MDA0NTUzOHww&ixlib=rb-4.1.0&q=85"
          alt="Business meeting"
          className="w-full h-full object-cover"
        />
        <div className="auth-image-overlay flex flex-col justify-end p-12">
          <blockquote className="text-white space-y-4">
            <p className="text-2xl font-medium leading-relaxed">
              "La sécurité de vos données est notre priorité."
            </p>
            <footer className="text-slate-300">
              — Équipe Espace Employés
            </footer>
          </blockquote>
        </div>
      </div>
    </div>
  );
}
